import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Filter, Search, UserCheck, X } from 'lucide-react';
import axios from '../../configApi/axiosConfig';
import { APIURL } from '../../configApi/apiConfig';
import { ClientesAprobadosDetalle } from './ClientesAprobadosDetalle';
import { GestionVentaForm } from './GestionVentaForm';
import { GestionVentaHistorial } from './GestionVentaHistorial';
import { ReasignarReiterativoForm } from './ReasignarReiterativoForm';

const TABS = [
  { value: 'PENDIENTE', label: 'Pendientes', counter: 'pendientes' },
  { value: 'PROGRAMADA', label: 'Programadas', counter: 'programadas' },
  { value: 'CERRADA', label: 'Cerradas', counter: 'cerradas' },
];

export function ClientesAprobadosModal({ idVendedor, nombreVendedor, onClose }) {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const [vista, setVista] = useState('cola');
  const [seleccionada, setSeleccionada] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [estadoCola, setEstadoCola] = useState('PENDIENTE');
  const [nivel, setNivel] = useState('');
  const [busquedaInput, setBusquedaInput] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanoPagina] = useState(10);
  const [notice, setNotice] = useState('');
  const [estado, setEstado] = useState({ loading: true, error: '', data: [], total: 0, puedeReasignar: false, contadores: { pendientes: 0, programadas: 0, cerradas: 0 } });
  const [catalogos, setCatalogos] = useState({ loading: true, error: '', niveles: [], subniveles: [], formasAtencion: [], rutas: [], productos: [] });

  const requestClose = useCallback(() => {
    if (dirty && !window.confirm('Hay cambios sin guardar. ¿Deseas salir de todas formas?')) return;
    onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    modalRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocusRef.current?.focus?.();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (vista !== 'cola') {
          if (!dirty || window.confirm('Hay cambios sin guardar. ¿Deseas volver a la cola?')) {
            setDirty(false); setVista('cola'); setSeleccionada(null);
          }
        } else requestClose();
      }
      if (event.key === 'Tab' && modalRef.current) {
        const focusable = [...modalRef.current.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
          .filter(element => element.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dirty, requestClose, vista]);

  useEffect(() => {
    const timer = setTimeout(() => { setBusqueda(busquedaInput.trim()); setPagina(1); }, 350);
    return () => clearTimeout(timer);
  }, [busquedaInput]);

  useEffect(() => {
    let active = true;
    Promise.all([
      axios.get(APIURL.getGestorVentasCatalogos()),
      axios.get(APIURL.get_Productos()),
    ]).then(([catalogResponse, productsResponse]) => {
      if (!active) return;
      setCatalogos({
        loading: false,
        error: '',
        niveles: catalogResponse.data?.niveles || [],
        subniveles: catalogResponse.data?.subniveles || [],
        formasAtencion: catalogResponse.data?.formasAtencion || [],
        rutas: catalogResponse.data?.rutas || [],
        productos: Array.isArray(productsResponse.data) ? productsResponse.data : [],
      });
    }).catch(error => {
      if (!active) return;
      const message = error?.response?.data?.message;
      setCatalogos(current => ({ ...current, loading: false, error: Array.isArray(message) ? message.join(' ') : message || 'No fue posible cargar los catálogos del gestor.' }));
    });
    return () => { active = false; };
  }, []);

  const fetchCola = useCallback(async () => {
    if (!idVendedor) return;
    setEstado(current => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axios.get(APIURL.getGestorVentas({
        idVendedor,
        estado: estadoCola,
        idNivel: nivel || undefined,
        busqueda: busqueda || undefined,
        pagina,
        tamanoPagina,
      }));
      const result = response.data || {};
      setEstado({
        loading: false,
        error: '',
        data: Array.isArray(result.items) ? result.items : [],
        total: Number(result.total || 0),
        puedeReasignar: Boolean(result.puedeReasignar),
        contadores: result.contadores || { pendientes: 0, programadas: 0, cerradas: 0 },
      });
    } catch (error) {
      const message = error?.response?.data?.message;
      setEstado(current => ({ ...current, loading: false, data: [], total: 0, error: Array.isArray(message) ? message.join(' ') : message || 'No fue posible cargar el gestor de ventas.' }));
    }
  }, [busqueda, estadoCola, idVendedor, nivel, pagina, tamanoPagina]);

  useEffect(() => { fetchCola(); }, [fetchCola]);

  const openGestion = row => { setSeleccionada(row); setDirty(false); setVista('formulario'); setNotice(''); };
  const openHistory = row => { setSeleccionada(row); setDirty(false); setVista('historial'); setNotice(''); };
  const openReasignar = row => { setSeleccionada(row); setDirty(false); setVista('reasignar'); setNotice(''); };
  const backToQueue = () => {
    if (dirty && !window.confirm('Hay cambios sin guardar. ¿Deseas volver a la cola?')) return;
    setDirty(false); setVista('cola'); setSeleccionada(null);
  };
  const saved = () => {
    setDirty(false); setVista('cola'); setSeleccionada(null); setNotice('Gestión guardada correctamente.'); fetchCola();
  };
  const savedReasignacion = () => {
    setDirty(false); setVista('cola'); setSeleccionada(null); setNotice('Reasignación guardada correctamente.'); fetchCola();
  };
  const totalPages = Math.max(1, Math.ceil(estado.total / tamanoPagina));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-slate-950/50 p-2 sm:p-4" onMouseDown={event => event.target === event.currentTarget && requestClose()}>
      <div ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="gestor-title" tabIndex={-1}
        style={{ width: '96vw', maxWidth: '1600px', height: '94vh', maxHeight: '94vh' }}
        className="flex min-h-0 flex-col overflow-hidden rounded-2xl bg-white shadow-2xl outline-none">
        <header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-morado"><UserCheck size={20} /></span>
            <div className="min-w-0">
              <h2 id="gestor-title" className="truncate text-base font-bold text-slate-900">Gestor de oportunidades</h2>
              <p className="truncate text-sm text-slate-600">{nombreVendedor}</p>
            </div>
          </div>
          <button type="button" onClick={requestClose} className="flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-morado/30" aria-label="Cerrar gestor"><X size={20} /></button>
        </header>

        <main className="min-h-0 flex-1 overflow-hidden p-4 sm:p-5">
          {vista === 'cola' && (
            <div className="flex h-full min-h-0 flex-col">
              {notice && <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800" role="status">{notice}</div>}
              <div className="flex shrink-0 flex-col gap-3 border-b border-slate-200 pb-4 xl:flex-row xl:items-end xl:justify-between">
                <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Estado de oportunidades">
                  {TABS.map(tab => (
                    <button key={tab.value} type="button" role="tab" aria-selected={estadoCola === tab.value}
                      onClick={() => { setEstadoCola(tab.value); setPagina(1); setNotice(''); }}
                      className={`min-h-11 cursor-pointer whitespace-nowrap rounded-lg px-3 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-morado/30 ${estadoCola === tab.value ? 'bg-morado text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
                      {tab.label} <span className={`ml-1 rounded-full px-1.5 py-0.5 text-xs ${estadoCola === tab.value ? 'bg-white/20' : 'bg-slate-200'}`}>{estado.contadores[tab.counter] || 0}</span>
                    </button>
                  ))}
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <label className="relative block">
                    <span className="sr-only">Buscar por cédula, nombre o teléfono</span>
                    <Search size={16} className="pointer-events-none absolute left-3 top-3.5 text-slate-400" />
                    <input value={busquedaInput} onChange={event => setBusquedaInput(event.target.value)} placeholder="Buscar cédula, nombre o teléfono" className="min-h-11 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm outline-none focus:border-morado focus:ring-2 focus:ring-morado/20 sm:w-72" />
                  </label>
                  <label className="relative block">
                    <span className="sr-only">Filtrar por nivel</span><Filter size={15} className="pointer-events-none absolute left-3 top-3.5 text-slate-400" />
                    <select value={nivel} onChange={event => { setNivel(event.target.value); setPagina(1); }} className="min-h-11 w-full cursor-pointer appearance-none rounded-lg border border-slate-300 bg-white pl-9 pr-8 text-sm outline-none focus:border-morado focus:ring-2 focus:ring-morado/20 sm:w-48">
                      <option value="">Todos los niveles</option>{catalogos.niveles.map(item => <option key={item.idNivel} value={item.idNivel}>{item.prioridad} · {item.nombre}</option>)}
                    </select>
                  </label>
                </div>
              </div>

              {catalogos.error && <div className="mt-4 shrink-0 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{catalogos.error}</div>}
              <div className="mt-4 min-h-0 flex-1"><ClientesAprobadosDetalle estado={estado} onGestionar={openGestion} onHistorial={openHistory} onReasignar={openReasignar} /></div>
              {!estado.loading && estado.total > 0 && (
                <div className="mt-4 flex shrink-0 flex-col items-center justify-between gap-3 border-t border-slate-200 pt-4 text-sm sm:flex-row">
                  <span className="text-slate-600">Página {pagina} de {totalPages} · {estado.total} registros</span>
                  <div className="flex gap-2">
                    <button type="button" disabled={pagina <= 1} onClick={() => setPagina(value => value - 1)} className="inline-flex min-h-11 cursor-pointer items-center gap-1 rounded-lg border border-slate-300 px-3 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"><ChevronLeft size={16} /> Anterior</button>
                    <button type="button" disabled={pagina >= totalPages} onClick={() => setPagina(value => value + 1)} className="inline-flex min-h-11 cursor-pointer items-center gap-1 rounded-lg border border-slate-300 px-3 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">Siguiente <ChevronRight size={16} /></button>
                  </div>
                </div>
              )}
            </div>
          )}

          {vista === 'formulario' && seleccionada && (
            <div className="h-full min-h-0 overflow-y-auto overscroll-contain">
              {catalogos.loading ? <div className="flex min-h-full items-center justify-center p-8 text-center text-sm text-slate-600">Cargando formulario…</div> :
                <GestionVentaForm oportunidad={seleccionada} catalogos={catalogos} productos={catalogos.productos} onBack={backToQueue} onSaved={saved} onDirtyChange={setDirty} />}
            </div>
          )}
          {vista === 'historial' && seleccionada && (
            <div className="h-full min-h-0 overflow-y-auto overscroll-contain">
              <GestionVentaHistorial oportunidad={seleccionada} onBack={backToQueue} />
            </div>
          )}
          {vista === 'reasignar' && seleccionada && (
            <div className="h-full min-h-0 overflow-y-auto overscroll-contain">
              <ReasignarReiterativoForm oportunidad={seleccionada} onBack={backToQueue} onSaved={savedReasignacion} onDirtyChange={setDirty} />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
